import { Column } from "primereact/column";
import { DataTable } from "primereact/datatable";
import { Dialog } from "primereact/dialog";
import { useState } from "react";
import { Link } from "react-router-dom";
import { useGetRemitAccuracyExcludedQuery } from "store/queries/remitAccuracy";
import {
  IRemitAccuracyFilters,
  IRemitExcludedRow,
} from "types/RemitAccuracy/RemitAccuracy";

import {
  PverifyRequestLink,
  RemitClaimLink,
  RemitClaimModal,
} from "./RemitAccuracyLinks";
import {
  exclusionLabel,
  formatRemitValue,
  REMIT_BENEFIT_LABELS,
  REMIT_KIND_LABELS,
} from "./remitAccuracyWording";

const PAGE_SIZE = 25;

const orderBody = (row: IRemitExcludedRow) => (
  <Link to={`/orders/${row.orderId}`} target="_blank" rel="noopener noreferrer">
    Estimate
  </Link>
);

const requestBody = (row: IRemitExcludedRow) => (
  <PverifyRequestLink requestId={row.pverifyRequestId} />
);

const engineBody = (row: IRemitExcludedRow) =>
  row.picks
    .map(
      (pick) =>
        `${REMIT_BENEFIT_LABELS[pick.benefit]} ${formatRemitValue(
          pick.value,
          pick.benefit
        )} (${REMIT_KIND_LABELS[pick.kind]})`
    )
    .join("; ") || "—";

const remitText = (row: IRemitExcludedRow) =>
  [
    row.coinsurancePercent !== null
      ? `Co-insurance ${row.coinsurancePercent}%`
      : null,
    row.copayBenefit && row.copayAmount !== null
      ? `${REMIT_BENEFIT_LABELS[row.copayBenefit]} ${formatRemitValue(
          row.copayAmount,
          row.copayBenefit
        )}`
      : null,
  ]
    .filter(Boolean)
    .join("; ") || "—";

/** CLE-2818 — the remits one exclusion reason kept out of scoring. */
export function RemitAccuracyExcludedDialog({
  reason,
  filters,
  onHide,
}: {
  reason: string | null;
  filters: IRemitAccuracyFilters;
  onHide: () => void;
}) {
  const [offset, setOffset] = useState(0);
  const { data, isFetching } = useGetRemitAccuracyExcludedQuery(
    { ...filters, reason: reason ?? undefined, limit: PAGE_SIZE, offset },
    { skip: !reason }
  );
  const page = data?.data;
  const [openClaim, setOpenClaim] = useState<{
    claimId: string;
    accountId: string;
  } | null>(null);

  const remitBody = (row: IRemitExcludedRow) => (
    <RemitClaimLink
      claimId={row.claimId}
      text={remitText(row)}
      onOpen={(claimId) => setOpenClaim({ claimId, accountId: row.accountId })}
    />
  );

  return (
    <Dialog
      header={reason ? `Not scored: ${exclusionLabel(reason)}` : ""}
      visible={Boolean(reason)}
      style={{ width: "min(1200px, 95vw)" }}
      onHide={() => {
        setOffset(0);
        onHide();
      }}
    >
      <DataTable
        value={page?.rows ?? []}
        lazy
        paginator
        rows={PAGE_SIZE}
        first={offset}
        totalRecords={page?.total ?? 0}
        onPage={(event) => setOffset(event.first)}
        loading={isFetching}
        emptyMessage="No remits."
        data-testid="remitAccuracy.excludedRows"
      >
        <Column field="accountName" header="Account" />
        <Column field="serviceDate" header="Date of service" />
        <Column header="Estimate" body={orderBody} />
        <Column header="Pverify Request" body={requestBody} />
        <Column header="Engine" body={engineBody} />
        <Column header="Remit" body={remitBody} />
      </DataTable>
      {openClaim && (
        <RemitClaimModal
          claimId={openClaim.claimId}
          accountId={openClaim.accountId}
          onHide={() => setOpenClaim(null)}
        />
      )}
    </Dialog>
  );
}
